import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Pencil, Trash2, X, Save, ChevronDown, ChevronUp, Users, FolderOpen } from 'lucide-react';
import FileUpload from './FileUpload';

const LANGS = ['pt-BR', 'en', 'it'];
const emptyExp = { company: {}, logo: '', role: {}, period: {}, location: {}, description: {}, challenges: {}, lessons: {}, technologies: [], has_sub_projects: false, sort_order: 0, is_active: true };

const JsonInput = ({ label, value, onChange }) => (
  <div className="space-y-2">
    <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wide">{label}</label>
    {LANGS.map(lang => (
      <div key={lang} className="flex items-center space-x-2">
        <span className="text-gray-500 text-xs w-10 flex-shrink-0">{lang}</span>
        <input value={(value && value[lang]) || ''} onChange={e => onChange({ ...value, [lang]: e.target.value })}
          className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-lime-400" />
      </div>
    ))}
  </div>
);

const JsonTextarea = ({ label, value, onChange }) => (
  <div className="space-y-2">
    <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wide">{label}</label>
    {LANGS.map(lang => (
      <div key={lang} className="flex space-x-2">
        <span className="text-gray-500 text-xs w-10 flex-shrink-0 pt-2">{lang}</span>
        <textarea rows={2} value={(value && value[lang]) || ''} onChange={e => onChange({ ...value, [lang]: e.target.value })}
          className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-lime-400 resize-none" />
      </div>
    ))}
  </div>
);

const ExperiencesAdmin = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyExp);
  const [techInput, setTechInput] = useState('');
  const [subProjects, setSubProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const fetch = useCallback(async () => {
    const { data } = await supabase.from('experiences').select('*').order('sort_order');
    setItems(data || []);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const fetchRelated = async (expId) => {
    const [sp, cl] = await Promise.all([
      supabase.from('experience_sub_projects').select('*').eq('experience_id', expId).order('sort_order'),
      supabase.from('experience_clients').select('*').eq('experience_id', expId).order('sort_order')
    ]);
    setSubProjects(sp.data || []);
    setClients(cl.data || []);
  };

  const startEdit = (item) => {
    setEditing(item.id);
    setForm({ ...item });
    setTechInput((item.technologies || []).join(', '));
    fetchRelated(item.id);
  };

  const startNew = () => {
    setEditing('new');
    setForm({ ...emptyExp, sort_order: items.length + 1 });
    setTechInput('');
    setSubProjects([]);
    setClients([]);
  };

  const save = async () => {
    const techs = techInput.split(',').map(t => t.trim()).filter(Boolean);
    const payload = { ...form, technologies: techs };
    delete payload.id; delete payload.created_at; delete payload.updated_at;

    if (editing === 'new') {
      const { data } = await supabase.from('experiences').insert(payload).select();
      if (data && data[0]) {
        for (const sp of subProjects.filter(s => s._new)) {
          const { _new, id, ...spData } = sp;
          await supabase.from('experience_sub_projects').insert({ ...spData, experience_id: data[0].id });
        }
        for (const cl of clients.filter(c => c._new)) {
          const { _new, id, ...clData } = cl;
          await supabase.from('experience_clients').insert({ ...clData, experience_id: data[0].id });
        }
      }
    } else {
      await supabase.from('experiences').update(payload).eq('id', editing);
    }
    setEditing(null);
    fetch();
  };

  const remove = async (id) => {
    if (!window.confirm('Remover esta experiência?')) return;
    await supabase.from('experiences').delete().eq('id', id);
    fetch();
  };

  // Sub-project helpers
  const addSubProject = () => setSubProjects([...subProjects, { _new: true, id: Date.now(), client: {}, description: {}, technologies: [], sort_order: subProjects.length + 1 }]);
  const updateSubProject = (idx, field, val) => { const s = [...subProjects]; s[idx] = { ...s[idx], [field]: val }; setSubProjects(s); };
  const saveSubProject = async (sp) => {
    const { _new, id, ...data } = sp;
    if (_new) {
      await supabase.from('experience_sub_projects').insert({ ...data, experience_id: editing });
    } else {
      await supabase.from('experience_sub_projects').update(data).eq('id', id);
    }
    fetchRelated(editing);
  };
  const removeSubProject = async (sp) => {
    if (sp._new) { setSubProjects(subProjects.filter(s => s.id !== sp.id)); return; }
    await supabase.from('experience_sub_projects').delete().eq('id', sp.id);
    fetchRelated(editing);
  };

  // Client helpers
  const addClient = () => setClients([...clients, { _new: true, id: Date.now(), name: '', logo: '', sort_order: clients.length + 1 }]);
  const updateClient = (idx, field, val) => { const c = [...clients]; c[idx] = { ...c[idx], [field]: val }; setClients(c); };
  const saveClient = async (cl) => {
    const { _new, id, ...data } = cl;
    if (_new) {
      await supabase.from('experience_clients').insert({ ...data, experience_id: editing });
    } else {
      await supabase.from('experience_clients').update(data).eq('id', id);
    }
    fetchRelated(editing);
  };
  const removeClient = async (cl) => {
    if (cl._new) { setClients(clients.filter(c => c.id !== cl.id)); return; }
    await supabase.from('experience_clients').delete().eq('id', cl.id);
    fetchRelated(editing);
  };

  if (editing) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-white">{editing === 'new' ? 'Nova Experiência' : 'Editar Experiência'}</h1>
          <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-white"><X size={24} /></button>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <JsonInput label="Empresa" value={form.company} onChange={v => setForm({...form, company: v})} />
            <JsonInput label="Cargo" value={form.role} onChange={v => setForm({...form, role: v})} />
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <JsonInput label="Período" value={form.period} onChange={v => setForm({...form, period: v})} />
            <JsonInput label="Localização" value={form.location} onChange={v => setForm({...form, location: v})} />
            <div>
              <FileUpload currentUrl={form.logo} onUpload={url => setForm({...form, logo: url})} label="Logo da Empresa" folder="companies" />
            </div>
          </div>
          <JsonTextarea label="Descrição" value={form.description} onChange={v => setForm({...form, description: v})} />
          <div className="grid md:grid-cols-2 gap-6">
            <JsonTextarea label="Desafios" value={form.challenges} onChange={v => setForm({...form, challenges: v})} />
            <JsonTextarea label="Lições" value={form.lessons} onChange={v => setForm({...form, lessons: v})} />
          </div>
          <div>
            <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wide mb-2">Tecnologias (separadas por vírgula)</label>
            <input value={techInput} onChange={e => setTechInput(e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-lime-400" placeholder="Java, Spring Boot, Docker" />
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wide mb-2">Ordem</label>
              <input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-lime-400" />
            </div>
            <label className="flex items-center space-x-3 pt-6">
              <input type="checkbox" checked={form.has_sub_projects} onChange={e => setForm({...form, has_sub_projects: e.target.checked})} className="w-4 h-4 accent-lime-400" />
              <span className="text-gray-300 text-sm">Tem Sub-projetos</span>
            </label>
            <label className="flex items-center space-x-3 pt-6">
              <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} className="w-4 h-4 accent-lime-400" />
              <span className="text-gray-300 text-sm">Ativo</span>
            </label>
          </div>

          {/* Sub-projects */}
          {editing !== 'new' && (
            <div className="border-t border-gray-800 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold text-lime-400 flex items-center space-x-2"><FolderOpen size={18} /><span>Sub-projetos</span></h3>
                <button onClick={addSubProject} className="text-lime-400 hover:text-lime-300 text-sm flex items-center space-x-1"><Plus size={16} /><span>Adicionar</span></button>
              </div>
              <div className="space-y-4">
                {subProjects.map((sp, idx) => (
                  <div key={sp.id} className="bg-gray-800 border border-gray-700 rounded-xl p-4 space-y-3">
                    <JsonInput label="Cliente" value={sp.client} onChange={v => updateSubProject(idx, 'client', v)} />
                    <JsonTextarea label="Descrição" value={sp.description} onChange={v => updateSubProject(idx, 'description', v)} />
                    <div>
                      <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wide mb-2">Tecnologias</label>
                      <input value={(sp.technologies || []).join(', ')} onChange={e => updateSubProject(idx, 'technologies', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
                        className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-lime-400" />
                    </div>
                    <div className="flex space-x-2">
                      <button onClick={() => saveSubProject(sp)} className="px-4 py-2 bg-lime-400 text-black text-xs font-bold rounded-lg hover:bg-lime-300">Salvar</button>
                      <button onClick={() => removeSubProject(sp)} className="px-4 py-2 bg-red-900/50 text-red-400 text-xs font-bold rounded-lg hover:bg-red-900">Remover</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Clients */}
          {editing !== 'new' && (
            <div className="border-t border-gray-800 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold text-lime-400 flex items-center space-x-2"><Users size={18} /><span>Clientes</span></h3>
                <button onClick={addClient} className="text-lime-400 hover:text-lime-300 text-sm flex items-center space-x-1"><Plus size={16} /><span>Adicionar</span></button>
              </div>
              <div className="space-y-4">
                {clients.map((cl, idx) => (
                  <div key={cl.id} className="bg-gray-800 border border-gray-700 rounded-xl p-4 space-y-3">
                    <input value={cl.name} onChange={e => updateClient(idx, 'name', e.target.value)} placeholder="Nome do cliente"
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-lime-400" />
                    <FileUpload currentUrl={cl.logo} onUpload={url => updateClient(idx, 'logo', url)} label="Logo do Cliente" folder="clients" />
                    <div className="flex space-x-2">
                      <button onClick={() => saveClient(cl)} className="px-3 py-2 bg-lime-400 text-black text-xs font-bold rounded-lg hover:bg-lime-300">Salvar</button>
                      <button onClick={() => removeClient(cl)} className="px-3 py-2 bg-red-900/50 text-red-400 text-xs font-bold rounded-lg hover:bg-red-900">Remover</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex space-x-4 pt-4">
            <button onClick={save} className="px-8 py-3 bg-lime-400 text-black font-bold rounded-full hover:bg-lime-300 transition-all flex items-center space-x-2">
              <Save size={18} /><span>Salvar Experiência</span>
            </button>
            <button onClick={() => setEditing(null)} className="px-8 py-3 border border-gray-600 text-gray-300 rounded-full hover:border-gray-400">Cancelar</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Experiências</h1>
        <button onClick={startNew} className="flex items-center space-x-2 px-4 py-2 bg-lime-400 text-black font-bold rounded-full hover:bg-lime-300 text-sm">
          <Plus size={18} /><span>Nova</span>
        </button>
      </div>
      <div className="space-y-4">
        {items.map(item => (
          <div key={item.id} className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
            <div className="p-5 flex items-center justify-between cursor-pointer" onClick={() => { setExpanded(expanded === item.id ? null : item.id); if (expanded !== item.id) fetchRelated(item.id); }}>
              <div className="flex items-center space-x-4">
                {item.logo && <img src={item.logo} alt="" className="w-10 h-10 bg-white rounded-lg p-1 object-contain" onError={e => { e.target.style.display = 'none'; }} />}
                <div>
                  <h3 className="text-white font-bold">{item.company?.['pt-BR'] || JSON.stringify(item.company)}</h3>
                  <p className="text-gray-400 text-sm">{item.role?.['pt-BR']} • {item.period?.['pt-BR']}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`text-xs px-2 py-1 rounded ${item.is_active ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400'}`}>{item.is_active ? 'Ativo' : 'Inativo'}</span>
                <span className="text-gray-500 text-xs">#{item.sort_order}</span>
                {expanded === item.id ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
              </div>
            </div>
            {expanded === item.id && (
              <div className="border-t border-gray-800 p-5 bg-gray-950">
                <div className="flex flex-wrap gap-2 mb-4">{(item.technologies || []).map((t, i) => <span key={i} className="px-2 py-1 bg-lime-400/10 text-lime-400 text-xs rounded-full">{t}</span>)}</div>
                {subProjects.length > 0 && <div className="mb-4"><p className="text-gray-400 text-xs font-semibold mb-2">Sub-projetos: {subProjects.length}</p></div>}
                {clients.length > 0 && <div className="mb-4"><p className="text-gray-400 text-xs font-semibold mb-2">Clientes: {clients.map(c => c.name).join(', ')}</p></div>}
                <div className="flex space-x-3">
                  <button onClick={() => startEdit(item)} className="flex items-center space-x-2 px-4 py-2 bg-lime-400 text-black text-sm font-bold rounded-lg hover:bg-lime-300"><Pencil size={14} /><span>Editar</span></button>
                  <button onClick={() => remove(item.id)} className="flex items-center space-x-2 px-4 py-2 bg-red-900/50 text-red-400 text-sm font-bold rounded-lg hover:bg-red-900"><Trash2 size={14} /><span>Remover</span></button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExperiencesAdmin;
